import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuthContext } from '../providers';
import { profileService } from '../services/Profile/profileService';

export type ChatRoomTheme = 'dark' | 'light';

interface ChatRoomThemeContextType {
  theme: ChatRoomTheme;
  toggleTheme: () => void;
  setTheme: (theme: ChatRoomTheme) => void;
}

const ChatRoomThemeContext = createContext<ChatRoomThemeContextType | undefined>(undefined);

const THEME_STORAGE_KEY = 'chatRoomTheme';

export const ChatRoomThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuthContext();
  const [theme, setThemeState] = useState<ChatRoomTheme>(() => {
    // Instant paint from the local cache; the account's saved value (below)
    // then overrides it once the user is authenticated.
    const savedTheme = localStorage.getItem(THEME_STORAGE_KEY) as ChatRoomTheme;
    return savedTheme || 'light';
  });

  useEffect(() => {
    // Save theme to localStorage whenever it changes (fast local cache)
    localStorage.setItem(THEME_STORAGE_KEY, theme);

    // Apply global styles for app-wide impact
    if (theme === 'dark') {
      document.body.style.background = '#1a1a1a';
      document.body.style.color = '#ffffff';
      document.body.classList.add('dark-mode');
    } else {
      document.body.style.background = '#f5f7fa';
      document.body.style.color = '#333333';
      document.body.classList.remove('dark-mode');
    }
  }, [theme]);

  // On login, adopt the account's saved theme (the source of truth across
  // devices). Falls back silently to the local value if the fetch fails.
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    profileService.getThemePreference()
      .then((dbTheme) => {
        if (!cancelled && dbTheme) setThemeState(dbTheme);
      })
      .catch(() => { /* keep local value */ });
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  // Persist a change to the account (fire-and-forget) when logged in.
  const persistTheme = (next: ChatRoomTheme) => {
    if (isAuthenticated) {
      profileService.setThemePreference(next).catch((err) =>
        console.error('Failed to save theme preference:', err));
    }
  };

  const toggleTheme = () => {
    setThemeState((prevTheme) => {
      const next = prevTheme === 'dark' ? 'light' : 'dark';
      persistTheme(next);
      return next;
    });
  };

  const setTheme = (newTheme: ChatRoomTheme) => {
    setThemeState(newTheme);
    persistTheme(newTheme);
  };

  return (
    <ChatRoomThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </ChatRoomThemeContext.Provider>
  );
};

export const useChatRoomTheme = (): ChatRoomThemeContextType => {
  const context = useContext(ChatRoomThemeContext);
  if (!context) {
    throw new Error('useChatRoomTheme must be used within a ChatRoomThemeProvider');
  }
  return context;
};

